import React, { useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTag, faXmark, faQrcode } from '@fortawesome/free-solid-svg-icons';
import { createInstance, restartInstance } from '../../../services/api';
import { useTranslation } from 'react-i18next';
import useAuth from '../../../hooks/useAuth';
import './novaConect.css';
import { useNavigate } from 'react-router-dom';
import ConnectQrModal from '../ConnectQrModal';

const NovaConexao = ({ isOpen, onClose }) => {
  const [instanceName, setInstanceName] = useState('');
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [showQrModal, setShowQrModal] = useState(false);
  const [createdInstance, setCreatedInstance] = useState(null);
  const [isCreating, setIsCreating] = useState(false); // Proteção extra contra duplo clique
  const { t } = useTranslation();
  const { user } = useAuth();
  const navigate = useNavigate();

  const resetAll = () => {
    setInstanceName('');
    setLoading(false);
    setErrorMessage('');
    setShowQrModal(false);
    setCreatedInstance(null);
  };

  const handleGenerateQRCode = async () => {
    if (!instanceName.trim()) {
      alert(t('novaConexao.alertName'));
      return;
    }

    // Proteção contra duplo clique
    if (loading || isCreating) {
      console.warn('⚠️ [NovaConexao] Tentativa de criar instância enquanto já está criando. Bloqueado.');
      return;
    }

    setLoading(true);
    setIsCreating(true);
    setErrorMessage('');

    try {
      // 1. Criar a instância
      const result = await createInstance(instanceName.trim(), user?.email);

      // 2. Reiniciar a instância
      const instanceNameFromResult = result.instanceName || result._wappfy?.instanceName;
      const serverId = result.server_id || result._wappfy?.server_id || 1;

      if (instanceNameFromResult) {
        await restartInstance(instanceNameFromResult, serverId);
      }

      // 3. Preparar dados da instância para o modal QR
      const instanceData = {
        id: result.id || Date.now(),
        name: instanceName.trim(),
        instanceName: instanceNameFromResult,
        instance: instanceNameFromResult,
        rawData: {
          server_id: serverId
        }
      };

      setCreatedInstance(instanceData);

      // 4. Ir direto para o QR Code (pular modal de sucesso)
      setShowQrModal(true);

      // Não resetar aqui para manter os dados da instância
      setInstanceName('');
      setLoading(false);
      setErrorMessage('');
      
    } catch (error) {
      console.error('❌ [NovaConexao] Erro no fluxo de criação:', error);
      setErrorMessage(error.message || 'Erro ao criar instância');
    } finally {
      setLoading(false);
      setIsCreating(false);
    }
  };

  const handleQrModalClose = () => {
    setShowQrModal(false);
    setCreatedInstance(null);
    
    // Fechar o modal de nova conexão
    if (onClose) onClose();
    
    // Navegar para Home
    navigate('/');
  };



  if (!isOpen) return null;

  return (
    <>
      <div className="nova-conexao-container">
        <div className="nova-conexao-backdrop" onClick={onClose}></div>
        <div className="nova-conexao-modal">
          <div className="nova-conexao-header">
            <div className="header-content">
              <FontAwesomeIcon icon={faTag} className="header-icon" />
              <h2 className="header-title">{t('novaConexao.nameTitle')}</h2>
            </div>
            <button className="close-button" onClick={onClose}>
              <FontAwesomeIcon icon={faXmark} />
            </button>
          </div>
          <div className="nova-conexao-content">
            <div className="input-container">
              <div className="input-wrapper">
                <input
                  type="text"
                  placeholder={t('novaConexao.namePlaceholder')}
                  className="name-input"
                  value={instanceName}
                  onChange={(e) => setInstanceName(e.target.value)}
                  disabled={loading}
                />
              </div>
            </div>
            {errorMessage && (
              <div className="error-message" style={{ color: 'red', marginTop: '10px' }}>
                {errorMessage}
              </div>
            )}
            <div className="qr-button-container">
              <button
                className="qr-button"
                onClick={handleGenerateQRCode}
                disabled={!instanceName.trim() || loading}
              >
                <FontAwesomeIcon icon={faQrcode} className="qr-icon" />
                <span>{loading ? t('novaConexao.creating') : t('novaConexao.generate')}</span>
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Modal de QR Code */}
      {showQrModal && createdInstance && (
        <ConnectQrModal
          isOpen={showQrModal}
          onClose={handleQrModalClose}
          connection={createdInstance}
          navigate={null} // Não redirecionar automaticamente
        />
      )}
    </>
  );
};

export default NovaConexao;